import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Icon } from './Icon.jsx';
import Modal from './Modal.jsx';
import { AccountDialog, LogoutDialog, SecurityDialog } from './AccountDialogs.jsx';
import { useT, useLang, LANGUAGES, t as translate } from '../lib/i18n.jsx';
import { useTheme } from '../lib/theme.jsx';
import { useAuth } from '../lib/auth.jsx';
import { toast } from '../lib/toast.jsx';
import { useNotifications } from '../lib/notifications.js';

const SECTIONS = [
  { id: 'overview', path: '/', key: 'nav.overview' },
  { id: 'domains', path: '/domeinen', key: 'nav.domains' },
  { id: 'hosting', path: '/hosting', key: 'nav.hosting' },
  { id: 'email', path: '/e-mail', key: 'nav.email' },
  { id: 'backups', path: '/backups', key: 'nav.backups' },
  { id: 'billing', path: '/facturatie', key: 'nav.billing' },
];

function buildIndex(lang, navTo) {
  const L = (k) => translate(lang, k);

  const nav = SECTIONS.map((s) => ({
    kind: 'nav',
    icon: { overview: 'rotate', domains: 'globe', hosting: 'server', email: 'mail', backups: 'archive', billing: 'card' }[s.id],
    label: L(s.key),
    hint: L('topbar.search.goToPage'),
    keywords: [s.id, s.path],
    run: () => navTo(s.path),
  }));

  const actions = [
    { label: L('topbar.action.addDomain'), icon: 'plus', target: '/domeinen?action=addDomain', kw: ['add', 'domain', 'domein', 'toevoegen', 'register'] },
    { label: L('topbar.action.createMailbox'), icon: 'plus', target: '/e-mail?action=newEntry', kw: ['mailbox', 'create', 'nieuw', 'new', 'aanmaken'] },
    { label: L('topbar.action.addDnsRecord'), icon: 'settings', target: '/domeinen?action=dns', kw: ['dns', 'record'] },
    { label: L('topbar.action.restoreBackup'), icon: 'rotate', target: '/backups', kw: ['restore', 'herstel', 'terug', 'backup', 'snapshot'] },
    { label: L('topbar.action.newPlan'), icon: 'server', target: '/hosting?action=newPlan', kw: ['plan', 'pakket', 'hosting', 'new'] },
    { label: L('topbar.action.openInvoices'), icon: 'card', target: '/facturatie', kw: ['invoice', 'factuur', 'billing'] },
  ].map((a) => ({
    kind: 'action',
    icon: a.icon,
    label: a.label,
    hint: L('topbar.search.action'),
    keywords: [a.label, ...a.kw],
    run: () => navTo(a.target),
  }));

  return [...nav, ...actions];
}

function rank(item, q) {
  if (!q) return 0;
  const ql = q.toLowerCase();
  let score = 0;
  for (const k of item.keywords) {
    const s = String(k).toLowerCase();
    if (s === ql) score = Math.max(score, 100);
    else if (s.startsWith(ql)) score = Math.max(score, 70);
    else if (s.includes(ql)) score = Math.max(score, 40);
  }
  return score;
}


function isCurrent(path, pathname) {
  return path === '/' ? pathname === '/' : pathname.startsWith(path);
}

export default function Topbar() {
  const t = useT();
  const { lang, setLang } = useLang();
  const { dark, setDark } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [searchOpen, setSearchOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  // Dialogs live here, not inside the user menu, so closing the menu can never
  // unmount a dialog that is still in use.
  const [dialog, setDialog] = useState(null); // 'logout' | 'account' | 'security'
  const avatarRef = useRef(null);
  const notifBtnRef = useRef(null);

  const notifications = useNotifications(lang);
  const unread = notifications.items.filter((n) => !n.read).length;

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen(true);
        setNotifOpen(false);
        setUserOpen(false);
      } else if (e.key === '/' && document.activeElement === document.body) {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Close the mobile drawer on navigation.
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  const navTo = useCallback(
    (path) => {
      navigate(path);
      setSearchOpen(false);
      setNotifOpen(false);
    },
    [navigate],
  );
  const closeSearch = useCallback(() => setSearchOpen(false), []);
  const closeNotif = useCallback(() => setNotifOpen(false), []);
  const closeUser = useCallback(() => setUserOpen(false), []);

  const openNotif = (n) => {
    notifications.markRead([n.id]);
    navTo(n.target);
  };

  const userAction = (action) => {
    setUserOpen(false);
    if (action === 'billing') navigate('/facturatie');
    else setDialog(action);
  };

  return (
    <header className="topbar">
      <div className="topbar-inner">
        <button
          type="button"
          className="icon-btn nav-toggle"
          aria-label={t('topbar.menu.open')}
          aria-expanded={navOpen}
          onClick={() => setNavOpen(true)}
        >
          <Icon name="menu" size={18} />
        </button>

        <div className="brand">
          <div className="brand-mark">D</div>
          <span>DMRS</span>
          <span className="brand-sub" style={{ color: 'var(--ink-4)', fontWeight: 400, marginLeft: 4 }}>Console</span>
        </div>

        <nav className="topnav" aria-label={t('topbar.menu.main')}>
          {SECTIONS.map((s) => (
            <NavLink
              key={s.id}
              to={s.path}
              end={s.path === '/'}
              aria-current={isCurrent(s.path, location.pathname) ? 'page' : undefined}
            >
              {t(s.key)}
            </NavLink>
          ))}
        </nav>

        <div className="topbar-right">
          <button
            type="button"
            className="search-trigger"
            title={t('topbar.search.title')}
            aria-label={t('topbar.search.label')}
            onClick={() => setSearchOpen(true)}
          >
            <Icon name="search" size={14} />
            <span>{t('topbar.search.placeholder')}</span>
            <kbd>⌘K</kbd>
          </button>

          <div style={{ position: 'relative' }}>
            <button
              ref={notifBtnRef}
              type="button"
              className="icon-btn"
              title={t('topbar.notif.title')}
              aria-label={
                unread > 0 ? t('topbar.notif.labelUnread', { n: unread }) : t('topbar.notif.title')
              }
              aria-expanded={notifOpen}
              aria-haspopup="dialog"
              onClick={() => {
                setNotifOpen((v) => !v);
                setUserOpen(false);
              }}
            >
              <Icon name="bell" size={17} />
              {unread > 0 && <span className="notif-dot" aria-hidden="true" />}
            </button>
            {notifOpen && (
              <NotificationPanel
                state={notifications}
                anchorRef={notifBtnRef}
                onClose={closeNotif}
                onOpen={openNotif}
              />
            )}
          </div>

          <button
            type="button"
            className="icon-btn theme-btn"
            title={t('topbar.theme.toggle')}
            aria-label={t('topbar.theme.toggle')}
            onClick={() => setDark(!dark)}
          >
            <Icon name={dark ? 'sun' : 'moon'} size={17} />
          </button>

          <LangToggle lang={lang} setLang={setLang} />

          <div style={{ position: 'relative' }}>
            <button
              ref={avatarRef}
              type="button"
              className={'avatar ' + (userOpen ? 'is-open' : '')}
              title={user?.full_name || t('nav.account')}
              aria-label={t('topbar.user.menu')}
              aria-expanded={userOpen}
              aria-haspopup="menu"
              onClick={() => {
                setUserOpen((v) => !v);
                setNotifOpen(false);
              }}
            >
              {initials(user?.full_name || user?.email || 'U')}
            </button>
            {userOpen && <UserMenu anchorRef={avatarRef} onClose={closeUser} onAction={userAction} />}
          </div>
        </div>
      </div>

      {navOpen && (
        <MobileNav
          lang={lang}
          t={t}
          pathname={location.pathname}
          dark={dark}
          setDark={setDark}
          setLang={setLang}
          onClose={() => setNavOpen(false)}
        />
      )}
      {searchOpen && <SearchPalette lang={lang} navTo={navTo} onClose={closeSearch} />}
      {dialog === 'logout' && <LogoutDialog lang={lang} returnFocusRef={avatarRef} onClose={() => setDialog(null)} />}
      {dialog === 'account' && <AccountDialog lang={lang} returnFocusRef={avatarRef} onClose={() => setDialog(null)} />}
      {dialog === 'security' && <SecurityDialog lang={lang} returnFocusRef={avatarRef} onClose={() => setDialog(null)} />}
    </header>
  );
}

export function LangToggle({ lang, setLang, className = '' }) {
  const t = useT();
  return (
    <div className={`lang-toggle ${className}`.trim()} role="group" aria-label={t('lang.label')}>
      {LANGUAGES.map((l) => (
        <button
          key={l.code}
          type="button"
          aria-pressed={lang === l.code}
          onClick={() => setLang(l.code)}
          lang={l.code}
          title={l.name}
          aria-label={l.name}
        >
          {l.label}
        </button>
      ))}
    </div>
  );
}

function initials(name) {
  if (!name) return 'U';
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() || '').join('') || name[0].toUpperCase();
}

function MobileNav({ lang, t, pathname, dark, setDark, setLang, onClose }) {
  return (
    <Modal
      onClose={onClose}
      backdropClassName="mnav-bg"
      dialogClassName="mnav"
      ariaLabel={t('topbar.menu.label')}
    >
      <div className="mnav-head">
        <div className="brand">
          <div className="brand-mark">D</div>
          <span>DMRS</span>
        </div>
        <button type="button" className="icon-btn" aria-label={t('topbar.menu.close')} onClick={onClose}>
          <Icon name="x" size={18} />
        </button>
      </div>
      <nav aria-label={t('topbar.menu.main')}>
        {SECTIONS.map((s) => (
          <NavLink
            key={s.id}
            to={s.path}
            end={s.path === '/'}
            aria-current={isCurrent(s.path, pathname) ? 'page' : undefined}
            onClick={onClose}
          >
            {t(s.key)}
          </NavLink>
        ))}
      </nav>
      <div className="mnav-foot">
        <LangToggle lang={lang} setLang={setLang} />
        <button
          type="button"
          className="icon-btn"
          aria-label={t('topbar.theme.toggle')}
          onClick={() => setDark(!dark)}
        >
          <Icon name={dark ? 'sun' : 'moon'} size={17} />
        </button>
      </div>
    </Modal>
  );
}

function SearchPalette({ lang, navTo, onClose }) {
  const t = useT();
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const index = useMemo(() => buildIndex(lang, navTo), [lang, navTo]);
  const groups = useMemo(() => {
    const ql = q.trim();
    let pool;
    if (!ql) {
      pool = index;
    } else {
      pool = index
        .map((it) => ({ it, s: rank(it, ql) }))
        .filter((x) => x.s > 0)
        .sort((a, b) => b.s - a.s)
        .slice(0, 14)
        .map((x) => x.it);
    }
    const labels = { nav: t('topbar.search.pages'), action: t('topbar.search.actions') };
    return ['nav', 'action']
      .map((k) => ({ key: k, label: labels[k], items: pool.filter((x) => x.kind === k) }))
      .filter((g) => g.items.length > 0);
  }, [q, index, t]);

  const flat = useMemo(() => groups.flatMap((g) => g.items), [groups]);

  useEffect(() => {
    setActive(0);
  }, [q]);

  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-i="${active}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(flat.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      flat[active]?.run();
    }
  };

  let runningIdx = 0;
  return (
    <Modal
      onClose={onClose}
      backdropClassName="cmd-bg"
      dialogClassName="cmd"
      ariaLabel={t('topbar.search.label')}
      initialFocusRef={inputRef}
    >
      <div className="cmd-input">
        <Icon name="search" size={16} />
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKeyDown}
          role="combobox"
          aria-expanded="true"
          aria-controls="cmd-list"
          aria-activedescendant={flat.length ? `cmd-opt-${active}` : undefined}
          aria-label={t('topbar.search.inputLabel')}
          placeholder={t('topbar.search.inputPlaceholder')}
        />
        <kbd>esc</kbd>
      </div>
      <div className="cmd-list" ref={listRef} id="cmd-list" role="listbox">
        {groups.length === 0 && (
          <div className="cmd-empty">
            {t('topbar.search.noResults', { q })}
          </div>
        )}
        {groups.map((g) => (
          <div className="cmd-group" key={g.key} role="group" aria-label={g.label}>
            <div className="cmd-group-label">{g.label}</div>
            {g.items.map((it) => {
              const i = runningIdx++;
              return (
                <button
                  type="button"
                  key={i}
                  id={`cmd-opt-${i}`}
                  role="option"
                  aria-selected={i === active}
                  tabIndex={-1}
                  data-i={i}
                  className={'cmd-item ' + (i === active ? 'is-active' : '')}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => it.run()}
                >
                  <span className="cmd-ico"><Icon name={it.icon} size={14} /></span>
                  <span className="cmd-label">{it.label}</span>
                  <span className="cmd-hint">{it.hint}</span>
                </button>
              );
            })}
          </div>
        ))}
      </div>
      <div className="cmd-foot">
        <span><kbd>↑</kbd><kbd>↓</kbd> {t('topbar.search.navigate')}</span>
        <span><kbd>↵</kbd> {t('topbar.search.open')}</span>
        <span><kbd>esc</kbd> {t('topbar.search.close')}</span>
      </div>
    </Modal>
  );
}

// Closes a dropdown on Escape or a press outside both the panel and its
// trigger button (the trigger toggles on its own).
function useDismiss(ref, anchorRef, onClose) {
  useEffect(() => {
    const onDown = (e) => {
      if (ref.current?.contains(e.target) || anchorRef?.current?.contains(e.target)) return;
      onClose();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        onClose();
        anchorRef?.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [ref, anchorRef, onClose]);
}

function NotificationPanel({ state, anchorRef, onClose, onOpen }) {
  const t = useT();
  const ref = useRef(null);
  useDismiss(ref, anchorRef, onClose);
  const { items, loading, error, markRead } = state;
  const unread = items.filter((n) => !n.read).length;

  return (
    <div className="notif-panel" ref={ref} role="dialog" aria-label={t('topbar.notif.title')}>
      <div className="notif-h">
        <div>
          <div className="notif-title">{t('topbar.notif.title')}</div>
          <div className="notif-sub">
            {unread > 0 ? t('topbar.notif.unread', { n: unread }) : t('topbar.notif.allCaughtUp')}
          </div>
        </div>
        {unread > 0 && (
          <button type="button" className="notif-mark" onClick={() => markRead(items.map((n) => n.id))}>
            {t('topbar.notif.markAllRead')}
          </button>
        )}
      </div>
      <div className="notif-list">
        {loading ? (
          <div className="notif-empty">{t('common.loading')}</div>
        ) : error ? (
          <div className="notif-empty">{t('topbar.notif.loadError')}</div>
        ) : items.length === 0 ? (
          <div className="notif-empty">{t('topbar.notif.empty')}</div>
        ) : (
          items.map((n) => (
            <button
              type="button"
              key={n.id}
              className={'notif-item ' + (n.read ? '' : 'is-unread')}
              onClick={() => onOpen(n)}
            >
              <span className={`notif-ico tone-${n.tone}`}>
                {n.icon === 'ok' && <Icon name="check" size={13} stroke={2} />}
                {n.icon === 'warn' && <Icon name="alert" size={13} stroke={1.8} />}
                {n.icon === 'info' && <Icon name="info" size={13} stroke={1.8} />}
              </span>
              <span className="notif-body">
                <span className="notif-it">{n.title}</span>
                <span className="notif-sub2">{n.body}</span>
              </span>
              <span className="notif-when" />
              {!n.read && <span className="notif-unread-dot" aria-hidden="true" />}
            </button>
          ))
        )}
      </div>
    </div>
  );
}

function UserMenu({ anchorRef, onClose, onAction }) {
  const t = useT();
  const { dark, setDark } = useTheme();
  const { user } = useAuth();
  const ref = useRef(null);
  useDismiss(ref, anchorRef, onClose);

  useEffect(() => {
    ref.current?.querySelector('[role="menuitem"]')?.focus();
  }, []);

  const onMenuKey = (e) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const items = Array.from(ref.current.querySelectorAll('[role="menuitem"], [role="menuitemcheckbox"]'));
    const i = items.indexOf(document.activeElement);
    const next = e.key === 'ArrowDown' ? (i + 1) % items.length : (i - 1 + items.length) % items.length;
    items[next]?.focus();
  };

  const copyId = () => {
    if (user?.id != null && navigator.clipboard) {
      navigator.clipboard.writeText(String(user.id));
      toast(t('topbar.user.idCopied'));
    }
  };

  const name = user?.full_name || user?.email || t('topbar.user.customer');

  return (
    <div className="user-menu" ref={ref} role="menu" aria-label={t('topbar.user.menu')} onKeyDown={onMenuKey}>
      <div className="user-head">
        <div className="user-avatar" aria-hidden="true">{initials(name)}</div>
        <div className="user-meta">
          <div className="user-name">{name}</div>
          <div className="user-mail">{user?.email}</div>
          {user?.id != null && (
            <div className="user-id">
              {t('topbar.user.customer')} <span className="mono">kl-{String(user.id).padStart(4, '0')}</span>
              <button type="button" className="user-copy" onClick={copyId} title={t('topbar.user.copy')} aria-label={t('topbar.user.copyId')}>
                <Icon name="copy" size={11} />
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="user-section">
        <button type="button" role="menuitem" className="user-item" onClick={() => onAction('account')}>
          <Icon name="user" size={14} />
          <span>{t('account.title')}</span>
        </button>
        <button type="button" role="menuitem" className="user-item" onClick={() => onAction('billing')}>
          <Icon name="card" size={14} />
          <span>{t('nav.billing')}</span>
        </button>
        <button type="button" role="menuitem" className="user-item" onClick={() => onAction('security')}>
          <Icon name="shield" size={14} />
          <span>{t('account.security.title')}</span>
        </button>
      </div>

      <div className="user-section">
        <div className="user-row">
          <Icon name={dark ? 'moon' : 'sun'} size={14} />
          <span id="user-dark-label">{t('topbar.user.darkTheme')}</span>
          <button
            type="button"
            className={'user-switch ' + (dark ? 'is-on' : '')}
            role="menuitemcheckbox"
            aria-checked={dark}
            aria-labelledby="user-dark-label"
            onClick={() => setDark(!dark)}
          >
            <span />
          </button>
        </div>
      </div>

      <div className="user-section">
        <button type="button" role="menuitem" className="user-item user-item--danger" onClick={() => onAction('logout')}>
          <Icon name="logout" size={14} />
          <span>{t('account.logout.menu')}</span>
        </button>
      </div>
    </div>
  );
}
